import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type WorkspaceAgentMarkdownPillsContextValue = {
  /** Mid-stream, an unresolved moniker is not yet a failure. */
  isStreaming: boolean;
  pills: readonly WorkspaceAgentMessagePill[];
};

const defaultContextValue: WorkspaceAgentMarkdownPillsContextValue = {
  isStreaming: false,
  pills: [],
};

const WorkspaceAgentMarkdownPillsContext = createContext<WorkspaceAgentMarkdownPillsContextValue>(defaultContextValue);

export const useWorkspaceAgentMarkdownPills = () => {
  const context = useContext(WorkspaceAgentMarkdownPillsContext);

  if (context === defaultContextValue) {
    const error = new Error('useWorkspaceAgentMarkdownPills must be used within WorkspaceAgentMarkdownPillsProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent markdown pills context not found' });
  }

  return context;
};

export type WorkspaceAgentMarkdownPillsProviderProps = {
  children: ReactNode;
  isStreaming: boolean;
  pills: readonly WorkspaceAgentMessagePill[];
};

/**
 * Carries one message's pills to the chip renderer in the markdown component map. The map has to be
 * a module constant — react-markdown treats each entry as a component type, so rebuilding it per
 * message would remount every chip — which leaves context as the only way in.
 */
export const WorkspaceAgentMarkdownPillsProvider = memo(
  ({ children, isStreaming, pills }: WorkspaceAgentMarkdownPillsProviderProps) => {
    const contextValue = useMemo(() => ({ isStreaming, pills }), [isStreaming, pills]);

    return (
      <WorkspaceAgentMarkdownPillsContext.Provider value={contextValue}>
        {children}
      </WorkspaceAgentMarkdownPillsContext.Provider>
    );
  },
);

WorkspaceAgentMarkdownPillsProvider.displayName = 'WorkspaceAgentMarkdownPillsProvider';
